/**
 * Chat Component - Multimodal Conversation Interface
 *
 * Supports:
 * - Text messages with SSE streaming
 * - Image upload (drag & drop, click to select)
 * - Ghost Mode (Electron) screenshot capture
 * - Markdown rendering for responses
 * - Voice input via speech-to-text (microphone)
 * - Voice output via text-to-speech (speaker button)
 * - Conversation persistence to Firestore
 * - Resume existing conversations
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import {
  sendMessageStreamWithRetry,
  IntentResponse,
  StreamChunk,
  ChatMessage,
  MessageContext,
  ExecutionMode,
  fileToBase64,
  compressImage,
  WebSocketManager,
  WebSocketStatus,
  WebSocketMessage,
  checkPermissionRequired,
  A2AStatusEvent
} from '../services/api';
import { updateUserContext, getIdToken } from '../services/firebase';
import { createConversation, addMessage as addMessageToFirestore, getMessages, getConversation } from '../services/conversations';
import { connectProvider } from '../services/integrationsApi';
import {
  hasTerminalExecutionEvidence,
  normalizeDynamicViewPayload,
  normalizeMainCanvasPayload,
} from '../services/workspaceEnvelope';
import { TrustDripCard } from './TrustDripCard';
import { SuperJarvisPayCard, CartMandate } from './pay/SuperJarvisPayCard';
import { PostBookingConnections } from './PostBookingConnections';
import { ViewRecipe } from './dynamic-view/types';
import { DynamicViewV2 } from './dynamic-view/DynamicViewV2';
import type { BookingCardData } from './dynamic-view/DynamicViewV2';
import { useVoice } from '../hooks/useVoice';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './Chat.module.css';

// Environment detection - Check for wispr API exposed by Electron preload
const isElectron = typeof window !== 'undefined' && !!window.wispr?.isElectron;

/**
 * Format a timestamp as a relative time string ("just now", "2m ago", "1h ago", etc.)
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  image?: string;  // Base64 image data
  agent?: string;
  model?: string;
  tier?: string;
  timestamp: Date;
  metadata?: {
    confidence?: number;
    vision_used?: boolean;
    sources?: string[];
    lane_contributions?: Record<string, number>;
    proactive_actions?: Array<{
      label: string;
      action: string;
      navigation?: { path: string; prefill?: boolean; type?: string };
    }>;
    pay_request?: CartMandate;
    view_recipe?: ViewRecipe;
    post_booking_connections?: { message: string; services: string[]; type: string };
  };
}

interface ChatProps {
  user: User;
  onSignOut: () => void;
  initialMessage?: string;
  conversationId?: string;  // For resuming existing conversations
  executionMode?: ExecutionMode;  // Execution mode: auto, fast, or super_think
}

/**
 * Inject the SuperJarvis postMessage bridge into generative HTML before rendering in an iframe.
 * Intercepts data-action clicks, exposes window.superjarvis API, reports scrollHeight for auto-resize.
 */
function injectDVBridge(html: string, messageId: string): string {
  const bridgeScript = `
<script>
(function() {
  var SJ_SOURCE = 'superjarvis-dv';
  var MSG_ID = '${messageId}';

  window.superjarvis = {
    action: function(type, data) {
      parent.postMessage({ source: SJ_SOURCE, type: type, data: data }, '*');
    }
  };

  function reportHeight() {
    var h = document.documentElement.scrollHeight || document.body.scrollHeight;
    parent.postMessage({ source: SJ_SOURCE, type: 'resize', data: { messageId: MSG_ID, height: h } }, '*');
  }

  var ro = new ResizeObserver(function() { reportHeight(); });
  document.addEventListener('DOMContentLoaded', function() {
    ro.observe(document.body);
    reportHeight();
  });
  if (document.readyState !== 'loading') {
    ro.observe(document.body);
    setTimeout(reportHeight, 100);
    setTimeout(reportHeight, 500);
  }

  document.addEventListener('click', function(e) {
    var el = e.target;
    while (el && el !== document.body) {
      var action = el.getAttribute && el.getAttribute('data-action');
      if (action) {
        e.preventDefault();
        e.stopPropagation();
        var data = {};
        if (el.dataset) {
          for (var key in el.dataset) {
            if (key !== 'action') data[key] = el.dataset[key];
          }
        }
        var type = action;
        if (action === 'select' || action === 'select_flight') type = 'select_flight';
        else if (action === 'book') type = 'book';
        else if (action === 'refine') type = 'refine';
        else type = 'action';

        if (type === 'refine') {
          parent.postMessage({ source: SJ_SOURCE, type: 'refine', data: { query: data.query || el.textContent } }, '*');
        } else {
          parent.postMessage({ source: SJ_SOURCE, type: type, data: data }, '*');
        }
        return;
      }
      el = el.parentElement;
    }
  }, true);
})();
<` + `\/script>`;

  if (html.includes('</body>')) {
    return html.replace('</body>', bridgeScript + '</body>');
  }
  return html + bridgeScript;
}

/**
 * Render markdown content using react-markdown with GFM support.
 * Handles bold, italic, headers, tables, links, lists, code, and horizontal rules.
 */
function renderMarkdown(text: string): React.ReactNode {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <div className={styles.mainTitle}>{children}</div>,
        h2: ({ children }) => <div className={styles.sectionTitle}>{children}</div>,
        h3: ({ children }) => <div className={styles.sectionHeader}>{children}</div>,
        h4: ({ children }) => <div className={styles.sectionHeader}>{children}</div>,
        strong: ({ children }) => <span className={styles.emphasis}>{children}</span>,
        code: ({ children, className: codeClass }) => {
          if (codeClass) {
            return <pre className={styles.codeBlock}><code>{children}</code></pre>;
          }
          return <code className={styles.inlineCode}>{children}</code>;
        },
        pre: ({ children }) => <>{children}</>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className={styles.markdownTableWrapper}>
            <table className={styles.markdownTable}>{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead>{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr>{children}</tr>,
        th: ({ children }) => <th className={styles.tableHeader}>{children}</th>,
        td: ({ children }) => <td className={styles.tableCell}>{children}</td>,
        ul: ({ children }) => <ul className={styles.bulletList}>{children}</ul>,
        ol: ({ children }) => <ol className={styles.numberedList}>{children}</ol>,
        li: ({ children }) => (
          <li className={styles.listItem}>{children}</li>
        ),
        hr: () => <hr className={styles.divider} />,
        p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
      }}
    >
      {text}
    </Markdown>
  );
}

export const Chat: React.FC<ChatProps> = ({ user, onSignOut, initialMessage, conversationId: initialConversationId, executionMode = 'super' }) => {
  // Navigation for proactive actions
  const navigate = useNavigate();

  // Split layout: track the latest view_recipe for the DV canvas pane
  const [activeViewRecipe, setActiveViewRecipe] = useState<ViewRecipe | null>(null);

  // Conversation state
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(initialConversationId || null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(!!initialConversationId);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      content: `Hello${user.displayName ? ` ${user.displayName.split(' ')[0]}` : ''}! I'm SuperJarvis. I can help you with:\n\n• **Research** - "What is quantum computing?"\n• **Create** - "Build me a task tracker app"\n• **Schedule** - "Schedule a meeting tomorrow at 2pm"\n• **Vision** - Drop an image and ask about it!`,
      agent: 'GENESIS',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [attachedImage, setAttachedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [currentAgent, setCurrentAgent] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [streamElapsed, setStreamElapsed] = useState(0);

  // A2A: track which agents are currently processing
  const [activeAgents, setActiveAgents] = useState<Map<string, A2AStatusEvent>>(new Map());

  // Voice recording state (via useVoice hook — gateway/Gemini STT)
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);

  // WebSocket state
  const [wsStatus, setWsStatus] = useState<WebSocketStatus>('disconnected');
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());

  // Desktop-specific state (Electron)
  const [systemActionsExpanded, setSystemActionsExpanded] = useState(false);
  const [shellCommand, setShellCommand] = useState('');
  const [shellOutput, setShellOutput] = useState('');
  const [isExecutingCommand, setIsExecutingCommand] = useState(false);

  // Trust-Drip state (permission requests)
  const [trustDripRequest, setTrustDripRequest] = useState<{
    provider: string;
    scopes: string[];
    message: string;
    context: string;
  } | null>(null);
  const lastUserMessageRef = useRef<string>('');

  // Scroll-to-bottom tracking
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const chatRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null); // kept for compat
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wsManagerRef = useRef<WebSocketManager | null>(null);

  // We need to keep track of a pending payment mandate if we interrupt it for Trust Drip
  const [pendingPaymentMandate, setPendingPaymentMandate] = useState<string | null>(null);

  // Voice input via useVoice hook (MediaRecorder → gateway → Gemini STT)
  const { isRecording, isTranscribing, toggleRecording, playText: _playText, isPlaying: _isPlaying, stopPlaying: _stopPlaying } = useVoice({
    onTranscription: (text) => {
      setInput(text);
      inputRef.current?.focus();
    },
    onError: (error) => {
      console.error('[Chat] Voice error:', error);
      alert(error);
    },
  });

  // Vault API Callbacks
  const handlePaymentApprove = useCallback(async (mandateId: string, signature: string, mandateObj?: any) => {
    console.log(`[Vault] Mandate ${mandateId} signed with assertions:`, signature);

    if (mandateObj?.trust_drip_connections?.length > 0 && !pendingPaymentMandate) {
      // Intercept payment! Ask for connection first.
      const firstService = mandateObj.trust_drip_connections[0];
      setPendingPaymentMandate(mandateId); // Save what we were trying to pay
      setTrustDripRequest({
        provider: firstService.provider,
        scopes: [],
        message: "Before we finalize this booking, I can connect to your calendar and email to save the confirmation for you.",
        context: firstService.reason
      });
      return; // Stop payment execution for now
    }

    try {
      const token = await getIdToken();
      const gatewayUrl = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const approveResponse = await fetch(`${gatewayUrl}/pay/mandate/${mandateId}/approve`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({ signature })
      });

      if (!approveResponse.ok) {
        throw new Error(`Payment approval failed. Code: ${approveResponse.status}`);
      }

      const approvePayload = await approveResponse.json().catch(() => ({}));
      const approvalToken = approvePayload?.approval_token;
      const executeHeaders = approvalToken
        ? { ...authHeaders, 'X-Payment-Approval-Token': approvalToken }
        : authHeaders;

      const response = await fetch(`${gatewayUrl}/pay/mandate/${mandateId}/execute`, {
        method: 'POST',
        headers: executeHeaders,
        credentials: 'include',
        body: JSON.stringify({ signature })
      });

      if (!response.ok) {
        throw new Error(`Payment processing failed. Code: ${response.status}`);
      }

      const executePayload = await response.json().catch(() => ({}));
      const paymentUrl = executePayload?.payment_url;
      const stripeId = executePayload?.stripe_id;
      const receiptMsg: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: paymentUrl || stripeId
          ? `Payment execution confirmed for mandate \`${mandateId.substring(0, 8)}\`${stripeId ? ` (Stripe: ${stripeId})` : ''}${paymentUrl ? `. Receipt: ${paymentUrl}` : ''}`
          : `Payment execution confirmed for mandate \`${mandateId.substring(0, 8)}\`.`,
        agent: 'SuperJarvis Pay',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, receiptMsg]);
      setPendingPaymentMandate(null); // Clear pending state
    } catch (err: any) {
      console.error('[Vault] Error executing mandate:', err);
      const errMsg: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `SuperJarvis couldn't finalize the payment (\`${mandateId.substring(0, 8)}\`). Error: ${err.message}`,
        agent: 'SuperJarvis Pay',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errMsg]);
      throw err;
    }
  }, [pendingPaymentMandate]);

  const handlePaymentDecline = useCallback((mandateId: string) => {
    console.log(`[Vault] Mandate ${mandateId} declined by user`);
    const declineMsg: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: `I declined the payment request (\`${mandateId.substring(0, 8)}\`). Please cancel the checkout.`,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, declineMsg]);
  }, []);

  const handlePaymentUndo = useCallback((mandateId: string) => {
    console.log(`[Vault] Undo trigger for mandate ${mandateId}`);
    // TODO: Wire this to POST /api/v1/vault/mandate/reversal
    const undoMsg: Message = {
      id: Date.now().toString(),
      type: 'assistant',
      content: `I have instantly cancelled the transaction and issued a reversal to the merchant for mandate \`${mandateId.substring(0, 8)}\`.`,
      agent: 'SuperJarvis Pay',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, undoMsg]);
  }, []);

  // Booking action: triggers pay flow directly from BookingCard without re-executing pipeline
  const handleBookingAction = useCallback((bookingData: BookingCardData, payRequest?: CartMandate) => {
    if (payRequest) {
      // Pay request already exists — inject it as a new assistant message with the pay card
      const payMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `Ready to book **${bookingData.title}** via ${bookingData.provider} for ${bookingData.currency || '$'}${bookingData.price}. Please review and approve the payment below.`,
        agent: 'SuperJarvis Pay',
        timestamp: new Date(),
        metadata: { pay_request: payRequest }
      };
      setMessages(prev => [...prev, payMessage]);
    } else {
      // No pay_request yet — open the booking URL as fallback
      if (bookingData.bookingUrl) {
        window.open(bookingData.bookingUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }, []);


  // ── postMessage bridge: listen for messages from DV iframes ──
  const iframeHeightsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (!msg || msg.source !== 'superjarvis-dv') return;

      const guardTerminalAction = (actionLabel: string): boolean => {
        if (hasTerminalExecutionEvidence(activeViewRecipe)) {
          return true;
        }

        const truthMessage: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: `I can't ${actionLabel} from this Dynamic View yet because the backend has not logged provider-backed execution evidence for that terminal action.`,
          agent: 'SuperJarvis Stage',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, truthMessage]);
        return false;
      };

      switch (msg.type) {
        case 'select_flight': {
          if (!guardTerminalAction('select that booking option')) break;
          const bookingData: BookingCardData = {
            title: `${msg.data?.airline || 'Flight'} \u2014 ${msg.data?.flightId || ''}`,
            provider: msg.data?.airline || 'Airline',
            price: msg.data?.price || 0,
            currency: msg.data?.currency || '$',
            bookingUrl: msg.data?.bookingUrl || '',
            ...msg.data,
          };
          handleBookingAction(bookingData, msg.data?.pay_request);
          break;
        }
        case 'book': {
          if (!guardTerminalAction('complete that booking')) break;
          if (msg.data) {
            const payMessage: Message = {
              id: Date.now().toString(),
              type: 'assistant',
              content: `Ready to complete booking. Please review and approve the payment below.`,
              agent: 'SuperJarvis Pay',
              timestamp: new Date(),
              metadata: { pay_request: msg.data as CartMandate },
            };
            setMessages(prev => [...prev, payMessage]);
          }
          break;
        }
        case 'refine': {
          if (msg.data?.query) {
            setInput(msg.data.query);
            setTimeout(() => {
              const sendBtn = document.querySelector('[data-testid="send-button"]') as HTMLButtonElement;
              sendBtn?.click();
            }, 150);
          }
          break;
        }
        case 'resize': {
          if (msg.data?.messageId && typeof msg.data?.height === 'number') {
            iframeHeightsRef.current[msg.data.messageId] = msg.data.height;
            const el = document.getElementById(`dv-iframe-${msg.data.messageId}`);
            if (el) {
              el.style.height = `${msg.data.height + 16}px`;
            }
          }
          break;
        }
        case 'action': {
          console.log('[DV Bridge] Generic action:', msg.data);
          break;
        }
        default:
          console.log('[DV Bridge] Unknown message type:', msg.type, msg.data);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [activeViewRecipe, handleBookingAction, setInput]);

  // Load existing conversation if conversationId is provided
  useEffect(() => {
    if (initialConversationId && user) {
      loadConversation(initialConversationId);
    }
  }, [initialConversationId, user]);

  const loadConversation = async (convId: string) => {
    setIsLoadingConversation(true);
    try {
      // Get conversation metadata
      const conversation = await getConversation(user.uid, convId);
      if (!conversation) {
        console.error('[Chat] Conversation not found:', convId);
        setIsLoadingConversation(false);
        return;
      }

      // Get messages
      const firestoreMessages = await getMessages(user.uid, convId);

      if (firestoreMessages.length > 0) {
        // Convert Firestore messages to local Message format
        const loadedMessages: Message[] = firestoreMessages.map((msg, index) => ({
          id: msg.id || `loaded-${index}`,
          type: msg.role as 'user' | 'assistant' | 'system',
          content: msg.content,
          image: msg.image,
          agent: msg.agent,
          model: msg.model,
          tier: msg.tier,
          timestamp: msg.timestamp?.toDate() || new Date(),
          metadata: msg.metadata
        }));

        // Replace the welcome message with loaded messages
        setMessages(loadedMessages);
        setCurrentConversationId(convId);
      }
    } catch (error) {
      console.error('[Chat] Error loading conversation:', error);
    } finally {
      setIsLoadingConversation(false);
    }
  };

  // Track latest view_recipe for DV canvas pane
  useEffect(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const vr = messages[i].metadata?.view_recipe;
      if (vr) {
        setActiveViewRecipe(vr);
        return;
      }
    }
    setActiveViewRecipe(null);
  }, [messages]);

  // Auto-scroll to bottom (only if user hasn't scrolled up)
  const isNearBottom = useCallback(() => {
    const el = chatRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }, []);

  useEffect(() => {
    if (chatRef.current && isNearBottom()) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, streamingContent, isNearBottom]);

  // Track scroll position for scroll-to-bottom button
  const handleChatScroll = useCallback(() => {
    setShowScrollBtn(!isNearBottom());
  }, [isNearBottom]);

  const scrollToBottom = useCallback(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  // Track elapsed streaming time for progressive warnings
  useEffect(() => {
    if (!isLoading) {
      setStreamElapsed(0);
      return;
    }
    const interval = setInterval(() => setStreamElapsed(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Focus input on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const hasHandledInitialMessage = useRef(false);

  // Initialize WebSocket connection
  useEffect(() => {
    const wsManager = new WebSocketManager();
    wsManagerRef.current = wsManager;

    wsManager.connect({
      onOpen: () => {
        console.log('[Chat] WebSocket connected');
      },
      onMessage: (message: WebSocketMessage) => {
        // Handle incoming WebSocket messages
        if (message.type === 'message' && message.content) {
          // Add received message to chat
          const newMessage: Message = {
            id: Date.now().toString(),
            type: 'assistant',
            content: message.content,
            agent: 'GENESIS',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, newMessage]);
        }
      },
      onClose: () => {
        console.log('[Chat] WebSocket disconnected');
      },
      onError: (error) => {
        console.error('[Chat] WebSocket error:', error);
      },
      onStatusChange: (status: WebSocketStatus) => {
        setWsStatus(status);
      },
      onTyping: (userId: string) => {
        // Show typing indicator
        setTypingUsers(prev => new Set(prev).add(userId));

        // Remove typing indicator after 3 seconds
        setTimeout(() => {
          setTypingUsers(prev => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        }, 3000);
      }
    });

    // Cleanup on unmount
    return () => {
      wsManager.disconnect();
    };
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    try {
      const base64 = await fileToBase64(file);
      const compressed = await compressImage(base64);
      setAttachedImage(compressed);
    } catch (error) {
      console.error('Error processing image:', error);
    }
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    // Handle image files
    if (file.type.startsWith('image/')) {
      handleFileSelect(file);
      return;
    }

    // Desktop mode: Handle other file types
    if (isElectron) {
      try {
        // For non-image files, read and display file info in chat
        const filePath = (file as any).path || '';

        if (filePath) {
          const fileInfo = `File dropped: ${file.name}\nSize: ${(file.size / 1024).toFixed(1)} KB\nType: ${file.type || 'unknown'}\nPath: ${filePath}`;
          setInput(prev => prev + `\n${fileInfo}`);
        } else {
          setInput(prev => prev + `\nFile: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
        }
      } catch (error) {
        console.error('Error handling file drop:', error);
      }
    }
  }, [handleFileSelect]);

  // Ghost Mode: Capture screenshot (Electron only)
  const captureScreenshot = useCallback(async () => {
    if (!isElectron || !window.wispr) return;

    try {
      const result = await window.wispr.captureScreen();
      if (result.success && result.image) {
        setAttachedImage(result.image);
      } else if (result.error) {
        console.error('Screenshot error:', result.error);
      }
    } catch (error) {
      console.error('Error capturing screenshot:', error);
    }
  }, []);

  // Build chat history for API
  const buildHistory = useCallback((): ChatMessage[] => {
    return messages
      .filter(m => m.type !== 'system')
      .slice(-10)
      .map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: m.content
      }));
  }, [messages]);

  // Voice recording now handled by useVoice hook (see above)

  // Voice playback: Play AI response using SpeechSynthesis API
  const playResponse = useCallback(async (messageId: string, text: string) => {
    try {
      // Stop any currently playing speech
      window.speechSynthesis.cancel();
      setPlayingMessageId(messageId);

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      utterance.onend = () => {
        setPlayingMessageId(null);
      };

      utterance.onerror = () => {
        setPlayingMessageId(null);
        alert('Failed to play audio. Please try again.');
      };

      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Error playing response:', error);
      setPlayingMessageId(null);
      alert('Failed to generate speech. Please try again.');
    }
  }, []);

  // Save message to Firestore
  const saveMessageToFirestore = useCallback(async (
    convId: string,
    message: Message
  ) => {
    try {
      await addMessageToFirestore(user.uid, convId, {
        role: message.type as 'user' | 'assistant' | 'system',
        content: message.content,
        image: message.image,
        agent: message.agent,
        model: message.model,
        tier: message.tier,
        metadata: message.metadata
      });
    } catch (error) {
      console.error('[Chat] Error saving message to Firestore:', error);
    }
  }, [user.uid]);

  // Send message
  const handleSend = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if ((!text && !attachedImage) || isLoading) return;

    // Store for Trust-Drip context
    lastUserMessageRef.current = text;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: text || '(Image)',
      image: attachedImage || undefined,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setAttachedImage(null);
    setIsLoading(true);
    setStreamingContent('');
    setCurrentAgent(null);

    // Create or use existing conversation
    let convId = currentConversationId;
    if (!convId) {
      try {
        convId = await createConversation(user.uid, text || 'Image analysis');
        setCurrentConversationId(convId);
        console.log('[Chat] Created new conversation:', convId);
      } catch (error) {
        console.error('[Chat] Error creating conversation:', error);
      }
    }

    // Save user message to Firestore
    if (convId) {
      saveMessageToFirestore(convId, userMessage);
    }

    // Update user context in Firestore
    updateUserContext(user.uid, {
      lastIntent: text,
      messageCount: messages.length + 1
    });

    // Build context for multimodal with execution mode
    const context: MessageContext = {
      mode: 'chat',
      executionMode: executionMode  // Pass execution mode to 4-lane broadcast system
    };

    if (userMessage.image) {
      context.image = userMessage.image;
    }

    // NOTE: WebSocket disabled temporarily - using HTTP streaming for reliability
    // The WebSocket service needs proper deployment and testing
    // TODO: Re-enable once WebSocket backend is verified working
    const useWebSocket = false; // Disabled for now
    const wsManager = wsManagerRef.current;
    if (useWebSocket && wsManager?.isConnected()) {
      try {
        console.log('[Chat] Sending via WebSocket...');
        wsManager.send({
          type: 'message',
          content: text || 'What is in this image?',
          data: {
            image: userMessage.image,
            history: buildHistory(),
            context
          }
        });

        // WebSocket message sent successfully
        // Response will be handled by onMessage handler
        // Note: Keep loading state for WebSocket until response arrives
        // setIsLoading(false); // Don't set false here - wait for response
        return;
      } catch (error) {
        console.warn('[Chat] WebSocket send failed, falling back to HTTP:', error);
        // Continue to HTTP fallback below
      }
    }

    // Fallback to HTTP POST (SSE streaming)
    let fullResponse = '';
    const finalConvId = convId; // Capture for closure

    try {
      await sendMessageStreamWithRetry(
        text || 'What is in this image?',
        context,
        buildHistory(),
        // On chunk
        (chunk: StreamChunk) => {
          if (chunk.type === 'agent') {
            setCurrentAgent(chunk.data);
          } else if (chunk.type === 'content') {
            fullResponse += chunk.data;
            setStreamingContent(fullResponse);
          } else if (chunk.type === 'status' && chunk.statusEvent) {
            // A2A StatusEvent: update agent activity map
            const evt = chunk.statusEvent;
            setActiveAgents(prev => {
              const next = new Map(prev);
              if (evt.status === 'processing') {
                next.set(evt.agent_id, evt);
              } else {
                // complete or failed — remove from active
                next.delete(evt.agent_id);
              }
              return next;
            });
          } else if (chunk.type === 'dynamic_view_ready' && chunk.view_recipe) {
            // DV arrived async — update the last assistant message's metadata
            const viewRecipe = normalizeDynamicViewPayload(chunk.view_recipe);
            if (!viewRecipe) return;
            setMessages(prev => {
              const updated = [...prev];
              // Find the last assistant message and attach view_recipe
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].type === 'assistant') {
                  updated[i] = {
                    ...updated[i],
                    metadata: {
                      ...updated[i].metadata,
                      view_recipe: viewRecipe,
                    },
                  };
                  break;
                }
              }
              return updated;
            });
          }
        },
        // On complete
        (result: IntentResponse) => {
          // Check if permission is required (Trust-Drip)
          const permissionNeeded = checkPermissionRequired(result);
          if (permissionNeeded) {
            // Show Trust-Drip card instead of error
            setTrustDripRequest({
              provider: permissionNeeded.provider,
              scopes: permissionNeeded.requiredScopes,
              message: permissionNeeded.message,
              context: lastUserMessageRef.current
            });

            // Add a message explaining we need permission
            const permissionMessage: Message = {
              id: (Date.now() + 1).toString(),
              type: 'assistant',
              content: permissionNeeded.message || `I'd like to help with that, but I'll need access to your ${permissionNeeded.provider}. Would you like to connect it?`,
              agent: 'GENESIS',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, permissionMessage]);
            setStreamingContent('');
            setCurrentAgent(null);
            setIsLoading(false);
            return;
          }

          const responseContent = fullResponse ||
            (typeof result.result === 'string' ? result.result : JSON.stringify(result.result, null, 2));

          // Extract proactive actions from clarification response
          const proactiveActions = result.clarification?.proactive_actions || [];

          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            type: 'assistant',
            content: responseContent,
            agent: result.agent || currentAgent || 'GENESIS',
            model: result.model,
            tier: result.tier,
            timestamp: new Date(),
            metadata: {
              confidence: result.confidence,
              vision_used: result.vision_used,
              sources: result.sources,
              lane_contributions: result.lane_contributions,
              view_recipe: normalizeMainCanvasPayload(result) || result.view_recipe,
              pay_request: result.pay_request,
              proactive_actions: proactiveActions.length > 0 ? proactiveActions : undefined
            }
          };

          console.log('[Chat] Assistant message view_recipe:',
            assistantMessage.metadata?.view_recipe
              ? `type=${assistantMessage.metadata.view_recipe.type}, html_len=${(assistantMessage.metadata.view_recipe as any)?.html?.length || 0}`
              : 'none');

          setMessages(prev => [...prev, assistantMessage]);
          setStreamingContent('');
          setCurrentAgent(null);
          setActiveAgents(new Map());
          setIsLoading(false);

          // Save assistant message to Firestore
          if (finalConvId) {
            saveMessageToFirestore(finalConvId, assistantMessage);
          }

          // Update context with last agent
          updateUserContext(user.uid, {
            lastAgent: result.agent || 'GENESIS'
          });
        },
        // On error — distinguish timeout/network from other errors
        (error: Error) => {
          const isTimeout = (error as any).isTimeout === true;
          const isNetwork = error.message.includes('network error') ||
                            error.message.includes('Failed to fetch') ||
                            error.message.includes('disconnected unexpectedly');

          let content: string;
          if (isTimeout || isNetwork) {
            content = 'This is taking longer than expected. SuperJarvis is still working — please send your message again to retry.';
          } else {
            content = `SuperJarvis encountered an error: ${error.message}`;
          }

          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            type: 'system',
            content,
            timestamp: new Date()
          };

          setMessages(prev => [...prev, errorMessage]);
          setStreamingContent('');
          setCurrentAgent(null);
          setActiveAgents(new Map());
          setIsLoading(false);
        }
      );
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'system',
        content: `SuperJarvis couldn't connect. Please try again.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  }, [input, attachedImage, isLoading, messages.length, user.uid, buildHistory, currentAgent, currentConversationId, saveMessageToFirestore]);

  // Handle initial message from URL params, including OAuth-return resume flows.
  useEffect(() => {
    if (initialMessage && !hasHandledInitialMessage.current && !isLoading && !isLoadingConversation) {
      hasHandledInitialMessage.current = true;
      setInput(initialMessage);
      setTimeout(() => {
        handleSend(initialMessage);
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 500);
    }
  }, [initialMessage, isLoading, isLoadingConversation, handleSend]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Shift+Enter: browser default inserts newline in textarea — no action needed
  };

  // Auto-resize textarea as user types
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  };

  const suggestedPrompts = [
    { icon: '🔍', text: 'Research the latest AI breakthroughs' },
    { icon: '📅', text: "Show today's schedule and events" },
    { icon: '💻', text: 'Help me build a web app' },
    { icon: '✈️', text: 'Plan a weekend trip for me' },
  ];

  const quickActions = [
    'What can you help me with?',
    "Show today's events",
    'Research latest AI news',
    'Help me code something'
  ];

  // Determine if chat is in "empty" state (only the welcome message or no real conversation)
  const isEmptyChat = messages.length <= 1 && !isLoading;

  // Show loading state while loading conversation
  if (isLoadingConversation) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.splitLayout}>
    <div className={styles.chatPane}>
    <div
      className={`${styles.container} ${isDragging ? styles.dragging : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop overlay */}
      {isDragging && (
        <div className={styles.dropOverlay}>
          <div className={styles.dropMessage}>Drop image here</div>
        </div>
      )}

      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.title}>SuperJarvis</h1>

        {/* Connection Status Indicator - Only show when connected or connecting */}
        {wsStatus !== 'disconnected' && (
          <div className={styles.connectionStatus} title={`Real-time: ${wsStatus}`}>
            <div className={`${styles.statusDot} ${styles[`status-${wsStatus}`]}`} />
            <span className={styles.statusText}>{wsStatus === 'connected' ? 'Live' : 'Connecting...'}</span>
          </div>
        )}

        <div className={styles.userInfo}>
          {user.photoURL && (
            <img
              src={user.photoURL}
              alt={user.displayName || 'User'}
              className={styles.avatar}
            />
          )}
          <button onClick={onSignOut} className={styles.signOutBtn}>
            Sign Out
          </button>
        </div>
      </header>

      {/* Empty state — shown when no conversation yet */}
      {isEmptyChat && (
        <div className={styles.emptyState}>
          <div className={styles.emptyLogo}>SuperJarvis</div>
          <div className={styles.emptySubtitle}>Your AI-powered personal assistant</div>
          <div className={styles.suggestedPrompts}>
            {suggestedPrompts.map((prompt, idx) => (
              <button
                key={idx}
                className={styles.suggestedPrompt}
                onClick={() => {
                  setInput(prompt.text);
                  textareaRef.current?.focus();
                }}
              >
                {prompt.icon} {prompt.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <div className={styles.chatContainer} ref={chatRef} onScroll={handleChatScroll} style={isEmptyChat ? { display: 'none' } : undefined}>
        {messages.map(message => (
          <div
            key={message.id}
            className={`${styles.message} ${styles[message.type]}`}
          >
            {message.agent && (
              <span className={styles.agentBadge}>
                {message.agent}
                {message.tier && <span className={styles.tierBadge}>{message.tier.toUpperCase()}</span>}
              </span>
            )}

            {/* Show attached image */}
            {message.image && (
              <div className={styles.messageImage}>
                <img src={message.image} alt="Attached" />
              </div>
            )}

            <div className={styles.messageContent}>
              {renderMarkdown(message.content)}
            </div>

            {/* DV rendering moved to canvas pane (right side of split layout) */}

            {/* SuperJarvis Pay Vault Card Injection */}
            {message.metadata?.pay_request && (
              <SuperJarvisPayCard
                mandate={message.metadata?.pay_request}
                onApprove={(mandateId, signature) => handlePaymentApprove(mandateId, signature, message.metadata?.pay_request)}
                onDecline={handlePaymentDecline}
                onUndo={handlePaymentUndo}
              />
            )}

            {/* Post-booking Trust-Drip: connect calendar/email after payment */}
            {message.metadata?.post_booking_connections && (
              <PostBookingConnections
                message={message.metadata.post_booking_connections.message}
                services={message.metadata.post_booking_connections.services}
                onConnect={(service) => {
                  window.open(`/integrations?connect=${service}`, '_blank');
                }}
                onDismiss={() => {/* Dismiss handled by component */ }}
              />
            )}

            {/* Sources panel */}
            {message.type === 'assistant' && message.metadata?.sources && message.metadata.sources.length > 0 && (
              <div className={styles.sourcesPanel}>
                <span className={styles.sourcesIcon}>📎</span>
                <span className={styles.sourcesLabel}>{message.metadata.sources.length} Sources</span>
                <div className={styles.sourcesList}>
                  {message.metadata.sources.map((source, idx) => (
                    <a
                      key={idx}
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.sourceLink}
                    >
                      {new URL(source).hostname.replace('www.', '')}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Speaker button for assistant messages */}
            {message.type === 'assistant' && (
              <button
                className={`${styles.speakerButton} ${playingMessageId === message.id ? styles.playing : ''}`}
                onClick={() => playResponse(message.id, message.content)}
                title={playingMessageId === message.id ? 'Playing...' : 'Play response'}
              >
                🔊
              </button>
            )}

            {/* Metadata for assistant messages */}
            {message.type === 'assistant' && message.metadata?.confidence && (
              <div className={styles.metadata}>
                <span className={styles.confidence}>
                  {Math.round(message.metadata.confidence * 100)}% confidence
                </span>
                {message.metadata.vision_used && (
                  <span className={styles.visionBadge}>👁️ Vision</span>
                )}
              </div>
            )}

            {/* Proactive action buttons (for goal intents) */}
            {message.type === 'assistant' && message.metadata?.proactive_actions && message.metadata.proactive_actions.length > 0 && (
              <div className={styles.proactiveActions}>
                {message.metadata.proactive_actions.map((action, idx) => (
                  <button
                    key={idx}
                    className={styles.proactiveButton}
                    onClick={() => {
                      if (action.navigation) {
                        // Navigate to the path with prefill state
                        navigate(action.navigation.path, {
                          state: {
                            prefill: action.navigation.prefill,
                            type: action.navigation.type,
                            context: message.content  // Pass the conversation context
                          }
                        });
                      } else {
                        // Send the action as a new message
                        setInput(`Yes, ${action.label.toLowerCase()}`);
                      }
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}

            {/* Relative timestamp */}
            <div className={styles.timestamp}>{formatRelativeTime(message.timestamp)}</div>
          </div>
        ))}

        {/* Streaming response */}
        {streamingContent && (
          <div className={`${styles.message} ${styles.assistant}`}>
            {currentAgent && (
              <span className={styles.agentBadge}>{currentAgent}</span>
            )}
            <div className={styles.messageContent}>
              {renderMarkdown(streamingContent)}
              <span className={styles.cursor}>▌</span>
            </div>
          </div>
        )}

        {/* Loading indicator with progressive warnings + A2A agent activity */}
        {isLoading && !streamingContent && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <span className={styles.agentBadge}>
              {currentAgent || 'SuperJarvis is thinking...'}
            </span>
            <div className={styles.loadingDots}>
              <span>•</span><span>•</span><span>•</span>
            </div>
            {/* A2A: show which agents are actively processing */}
            {activeAgents.size > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Array.from(activeAgents.values()).map(evt => (
                  <span key={evt.agent_id} style={{
                    padding: '3px 10px', borderRadius: 12,
                    background: 'rgba(6, 182, 212, 0.12)', border: '1px solid rgba(6, 182, 212, 0.3)',
                    fontSize: 11, color: '#22d3ee',
                  }}>
                    ⚡ {evt.message}
                  </span>
                ))}
              </div>
            )}
            {streamElapsed >= 30 && streamElapsed < 45 && (
              <div style={{ marginTop: 8, padding: '6px 12px', borderRadius: 8, background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.2)', fontSize: 12, color: '#eab308' }}>
                Still working — complex requests can take up to a minute...
              </div>
            )}
            {streamElapsed >= 45 && (
              <div style={{ marginTop: 8, padding: '6px 12px', borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', fontSize: 12, color: '#f87171' }}>
                Taking longer than usual — hang tight, retrying if needed...
              </div>
            )}
          </div>
        )}

        {/* Typing indicator */}
        {typingUsers.size > 0 && (
          <div className={styles.typingIndicator}>
            SuperJarvis is typing...
          </div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollBtn && (
        <button className={styles.scrollToBottom} onClick={scrollToBottom} title="Scroll to bottom">
          ↓
        </button>
      )}

      {/* Quick Actions — only show when empty state is not visible */}
      {!isEmptyChat && messages.length <= 2 && (
        <div className={styles.quickActions}>
          {quickActions.map((action, index) => (
            <button
              key={index}
              className={styles.quickAction}
              onClick={() => setInput(action)}
            >
              {action}
            </button>
          ))}
        </div>
      )}

      {/* Attached Image Preview */}
      {attachedImage && (
        <div className={styles.attachedPreview}>
          <img src={attachedImage} alt="Attached" />
          <button
            className={styles.removeAttachment}
            onClick={() => setAttachedImage(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className={styles.inputContainer}>
        {/* Image attachment button */}
        <button
          className={styles.attachButton}
          onClick={() => fileInputRef.current?.click()}
          title="Attach image"
        >
          📷
        </button>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
        />

        {/* Ghost Mode screenshot button (Electron only) */}
        {isElectron && (
          <button
            className={styles.screenshotButton}
            onClick={captureScreenshot}
            title="Capture screenshot"
          >
            📸
          </button>
        )}

        <div className={styles.inputWrapper}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyPress}
            placeholder="Ask SuperJarvis anything..."
            className={styles.textareaInput}
            disabled={isLoading}
            rows={1}
          />
          {input.length > 200 && (
            <span className={`${styles.charCount} ${input.length > 4000 ? styles.danger : input.length > 2000 ? styles.warning : ''}`}>
              {input.length.toLocaleString()}
            </span>
          )}
        </div>

        {/* Voice recording button (useVoice — gateway/Gemini STT) */}
        <button
          className={`${styles.voiceButton} ${isRecording ? styles.recording : ''}`}
          onClick={toggleRecording}
          disabled={isTranscribing}
          title={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Start voice recording'}
        >
          {isTranscribing ? '...' : '🎤'}
        </button>

        <button
          onClick={() => handleSend()}
          disabled={isLoading || (!input.trim() && !attachedImage)}
          className={styles.sendButton}
        >
          Send
        </button>
      </div>

      {/* Desktop System Actions (Electron only) */}
      {isElectron && (
        <div className={styles.systemActions}>
          <button
            className={styles.systemToggle}
            onClick={() => setSystemActionsExpanded(!systemActionsExpanded)}
          >
            {systemActionsExpanded ? '▼ System Actions' : '▶ System Actions'}
          </button>

          {systemActionsExpanded && (
            <div className={styles.systemPanel}>
              <div className={styles.shellInput}>
                <input
                  type="text"
                  value={shellCommand}
                  onChange={(e) => setShellCommand(e.target.value)}
                  placeholder="Enter shell command..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && shellCommand.trim()) {
                      // Execute shell command via Electron
                      setIsExecutingCommand(true);
                      window.wispr?.executeCommand?.(shellCommand)
                        .then((result) => {
                          setShellOutput(result.success ? (result.stdout || 'Command executed successfully') : (result.error || 'Command failed'));
                          setIsExecutingCommand(false);
                        })
                        .catch((error: Error) => {
                          setShellOutput(`Error: ${error.message}`);
                          setIsExecutingCommand(false);
                        });
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (shellCommand.trim()) {
                      setIsExecutingCommand(true);
                      window.wispr?.executeCommand?.(shellCommand)
                        .then((result) => {
                          setShellOutput(result.success ? (result.stdout || 'Command executed successfully') : (result.error || 'Command failed'));
                          setIsExecutingCommand(false);
                        })
                        .catch((error: Error) => {
                          setShellOutput(`Error: ${error.message}`);
                          setIsExecutingCommand(false);
                        });
                    }
                  }}
                  disabled={isExecutingCommand}
                >
                  {isExecutingCommand ? '...' : 'Run'}
                </button>
              </div>
              {shellOutput && (
                <pre className={styles.shellOutput}>{shellOutput}</pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Trust-Drip Permission Card */}
      {trustDripRequest && (
        <TrustDripCard
          provider={trustDripRequest.provider}
          context={trustDripRequest.context}
          customMessage={trustDripRequest.message}
          onGrant={async () => {
            const provider = trustDripRequest.provider;
            const result = await connectProvider(provider);

            if (result.success && result.auth_url) {
              sessionStorage.setItem('pendingTrustDripProvider', provider);
              if (lastUserMessageRef.current) {
                sessionStorage.setItem('pendingTrustDripPrompt', lastUserMessageRef.current);
              }
              window.location.href = result.auth_url;
              return;
            }

            const grantMessage: Message = {
              id: Date.now().toString(),
              type: 'assistant',
              content: result.success
                ? (result.message || `I'm setting up ${provider}. Try again in a moment.`)
                : (result.error || `I couldn't start the ${provider} connection yet.`),
              agent: 'GENESIS',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, grantMessage]);
            setTrustDripRequest(null);
          }}
          onDeny={() => {
            // Add a message acknowledging the denial
            const denyMessage: Message = {
              id: Date.now().toString(),
              type: 'assistant',
              content: `No problem! I'll continue without ${trustDripRequest.provider} access. You can always connect it later in Settings if you change your mind.`,
              agent: 'GENESIS',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, denyMessage]);
            setTrustDripRequest(null);

            // If we were interrupting a payment, resume it anyway (they declined connection)
            if (pendingPaymentMandate) {
              handlePaymentApprove(pendingPaymentMandate, "skipped_signature", { trust_drip_connections: [] });
              setPendingPaymentMandate(null);
            }
          }}
        />
      )}
    </div>
    </div>

    {/* Right Pane: Dynamic View Canvas */}
    <div className={styles.dvCanvas}>
      {activeViewRecipe?.type === 'generative_html' && (activeViewRecipe as any).html ? (
        <iframe
          id="dv-canvas-iframe"
          srcDoc={injectDVBridge((activeViewRecipe as any).html, 'canvas')}
          style={{
            width: '100%',
            height: iframeHeightsRef.current['canvas']
              ? `${iframeHeightsRef.current['canvas'] + 16}px`
              : '100%',
            minHeight: '480px',
            border: 'none',
            background: '#0a0a0f',
            borderRadius: '16px'
          }}
          title="SuperJarvis Dynamic View"
        />
      ) : activeViewRecipe?.blocks ? (
        <div style={{ width: '100%', height: '100%', overflow: 'auto', padding: '1rem' }}>
          <DynamicViewV2
            recipe={activeViewRecipe}
            onBookAction={(bookingData) => handleBookingAction(bookingData)}
          />
        </div>
      ) : (
        <div className={styles.dvCanvasEmpty}>
          <div className={styles.dvCanvasEmptyLogo}>SuperJarvis</div>
          <div className={styles.dvCanvasEmptyText}>Dynamic View will appear here</div>
        </div>
      )}
    </div>

    </div>
  );
};
